import React, { useCallback, useEffect, useMemo, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { DATATEST } from "constants/integration";
import { checkVisibilityRules, IAttribute, IBinding, mapByKey } from "constants/product-customizations/customization";
import {
    MetaSelectionType,
    TMetaCustomizationSelection,
    TPrintedImageCustomizationSelection,
    TPrintedTextCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import { MetadataTextAttribute } from "./metadata-text-attribute";
import { MetadataSelectableAttribute } from "./metadata-selectable-attribute";
import { useCustomizationModalContext } from "../context/customization-modal-context";
import theme from "styles/theme";

type TChildAttributeSelection = TPrintedImageCustomizationSelection | TPrintedTextCustomizationSelection;

interface IProps {
    childAttributes: IAttribute[];
    selected: TChildAttributeSelection;
    readonly: boolean;
    attributeId: string;
    nestedValues: TMetaCustomizationSelection[];
    onChange: (newNestedValues: TMetaCustomizationSelection[]) => void;
    onValidationError?: (attributeId: string, message: string | null) => void;
}

type TOnChildAttributeChangeArgs = {
    value: string;
    valueKey: string;
    displayValue: string;
} & ({} | { valueId: string; target?: IBinding });

export const ChildAttributes: React.FC<IProps> = ({
    childAttributes,
    selected,
    readonly,
    attributeId,
    nestedValues,
    onChange,
}) => {
    const classes = useStyles();
    const [currentSelected, setCurrentSelected] = useState<TChildAttributeSelection>();
    const { printSettings } = useCustomizationModalContext();

    const selectedNestedValues = selected.nestedValues;
    const defaultedNestedValuesMap = useMemo(
        () => mapByKey([...nestedValues, ...selectedNestedValues], "attributeId"),
        [nestedValues, selectedNestedValues]
    );

    useEffect(() => {
        if (selected) {
            setCurrentSelected(selected);
        }
    }, [selected]);

    const onChildAttributeChange = useCallback(
        (args: TOnChildAttributeChangeArgs) => {
            const index = nestedValues.findIndex((value) => value.valueKey === args.valueKey);
            if (index < 0) {
                return;
            }

            const newNestedValues = [...nestedValues];
            const nestedValue = newNestedValues[index];
            nestedValue.displayValue = args.displayValue;
            nestedValue.value = args.value;
            if (nestedValue.type === MetaSelectionType.SELECTABLE && "valueId" in args) {
                nestedValue.target = args.target;
                nestedValue.valueId = args.valueId;
            }

            newNestedValues[index] = nestedValue;

            onChange(newNestedValues);
        },
        [nestedValues, onChange]
    );

    return (
        <div
            className={classes.childAttributesContainer}
            data-test={DATATEST.CUSTOMIZATIONS.CHILD_ATTRIBUTES_CONTAINER}
        >
            {nestedValues?.length > 0
                ? childAttributes.map((attribute) => {
                      const isVisible = checkVisibilityRules(
                          currentSelected,
                          attribute?.metadata?.visibilityRules,
                          printSettings
                      );

                      if (!isVisible) {
                          return null;
                      }

                      const selection = defaultedNestedValuesMap.get(attribute._id);
                      if (!selection) {
                          return null;
                      }

                      if (readonly && !selection.displayValue) {
                          return null;
                      }

                      const index = Number(selection.valueKey);

                      if (attribute?.metadata?.text) {
                          return (
                              <MetadataTextAttribute
                                  key={index}
                                  value={selection?.displayValue || ""}
                                  onChange={onChildAttributeChange}
                                  label={attribute?.name}
                                  index={index}
                                  readonly={readonly}
                                  maxLimit={250}
                              />
                          );
                      }

                      if (attribute?.metadata?.selectable) {
                          if (!selection || selection.type !== MetaSelectionType.SELECTABLE) {
                              return null;
                          }

                          return (
                              <MetadataSelectableAttribute
                                  key={index}
                                  attributeId={attributeId}
                                  value={selection}
                                  attributeValue={attribute?.metadata?.selectable}
                                  onChange={onChildAttributeChange}
                                  label={attribute?.name}
                                  index={index}
                                  readonly={readonly}
                                  target={selection?.target}
                              />
                          );
                      }

                      return null;
                  })
                : null}
        </div>
    );
};

const useStyles = makeStyles({
    attributeLabel: {
        marginBottom: "5px",
        color: theme.palette.textColor,
    },
    commentInput: {
        height: "20px",
    },
    childAttributesContainer: {
        display: "flex",
        flexWrap: "wrap",
        width: "100%",
    },
    childAttribute: {
        width: "47%",
        marginRight: "10px",
    },
});
